import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Plus, Minus, Trash, ShoppingBag, SpinnerGap, Tag, PenNib } from "@phosphor-icons/react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [email, setEmail] = useState(() => localStorage.getItem("vitae_cart_email") || "");

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  // Pack discount: sum of folder quantities across items.
  const folderQty = items
    .filter((i) => (i.kind || "folder") === "folder")
    .reduce((s, i) => s + i.quantity, 0);
  const tiers = pricing?.pack_tiers || [];
  const activeTier = tiers.find((t) => folderQty >= t.min_folders) || null;
  const packOff = activeTier?.off_pct || 0;
  const folderSubtotal = items
    .filter((i) => (i.kind || "folder") === "folder")
    .reduce((s, i) => s + i.price * i.quantity, 0);
  const packDiscount = (folderSubtotal * packOff) / 100;
  const grandTotal = Math.max(0, total - packDiscount);
  const allMagnets = items.length > 0 && items.every((i) => i.kind === "magnet");

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          kind: i.kind || "folder",
          model_id: i.model_id || null,
          size: i.size || null,
          finish: i.finish || null,
          pen_color: i.pen_color || null,
          magnet_design: i.magnet_design || null,
          upload_ids: i.upload_ids || [],
          specialties: i.specialties || [],
          name_personalization: i.name_personalization || null,
          gift_wrap: !!i.gift_wrap,
          gift_message: i.gift_message || null,
          quantity: (i.kind === "pen" || i.kind === "magnet") ? i.quantity * (i.pen_qty || i.magnet_qty || 1) : i.quantity,
        })),
        origin_url: window.location.origin,
      };
      // Optional email left in the cart: pre-fills Stripe and lets us send
      // the abandoned-cart reminder if they don't finish the purchase.
      const trimmed = email.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        payload.customer_email = trimmed;
        localStorage.setItem("vitae_cart_email", trimmed);
      }
      const { data } = await api.post("/payments/checkout", payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error("No se pudo iniciar el pago. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="bg-[#FAF9F6] border-l border-[#EBEBEB] w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#EBEBEB]">
          <SheetTitle className="font-display text-2xl text-[#2D3732] text-left font-medium">
            Tu carrito {count > 0 && <span className="text-[#8A9A6F]">({count})</span>}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Revisa los productos de tu carrito y finaliza la compra.
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
            <ShoppingBag size={44} weight="thin" className="text-[#A3B18A]" />
            <p className="text-[#6B705C]">Tu carrito está vacío.</p>
            <p className="text-sm text-[#9CA3AF]">
              Descubre nuestras colecciones y empieza a organizar tu tranquilidad.
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {items.map((item) => (
              <div key={item.key} data-testid={`cart-item-${item.slug}`} className="flex gap-4">
                <div
                  className="w-20 h-24 rounded-xl overflow-hidden bg-[#F6F1E7] flex-shrink-0 flex items-center justify-center"
                  style={!item.image && item.pen_hex ? { background: item.pen_hex } : undefined}
                >
                  {item.image ? (
                    <img src={item.image} alt={item.model_name} className="w-full h-full object-cover" />
                  ) : (
                    <PenNib size={26} weight="thin" className="text-[#2D3732]" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-display text-lg text-[#2D3732] leading-tight">
                    {item.model_name}
                  </h4>
                  <p className="text-xs text-[#6B705C] mt-0.5">
                    {item.size_label}
                    {item.finish_label ? ` · ${item.finish_label}` : ""}
                  </p>
                  {item.specialties && item.specialties.length > 0 && (
                    <p className="text-xs text-[#8A9A6F] mt-0.5">
                      + {item.specialties.join(", ")}
                    </p>
                  )}
                  {item.name_personalization && (
                    <p className="text-xs text-[#6B705C] mt-0.5 italic">
                      Nombre: {item.name_personalization}
                    </p>
                  )}
                  {item.gift_wrap && (
                    <p className="text-xs text-[#8A9A6F] mt-0.5">
                      🎁 Envuelto para regalo
                      {item.gift_message ? ` · «${item.gift_message.slice(0, 60)}${item.gift_message.length > 60 ? "…" : ""}»` : ""}
                    </p>
                  )}
                  <p className="text-sm text-[#4A554E] mt-1">{item.price.toFixed(2)} €</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center border border-[#EBEBEB] rounded-full bg-white">
                      <button
                        data-testid={`qty-minus-${item.slug}`}
                        onClick={() => updateQty(item.key, item.quantity - 1)}
                        className="p-2 text-[#6B705C] hover:text-[#2D3732]"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center text-sm">{item.quantity}</span>
                      <button
                        data-testid={`qty-plus-${item.slug}`}
                        onClick={() => updateQty(item.key, item.quantity + 1)}
                        className="p-2 text-[#6B705C] hover:text-[#2D3732]"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <button
                      data-testid={`remove-${item.slug}`}
                      onClick={() => removeItem(item.key)}
                      className="text-[#9CA3AF] hover:text-[#b05656] transition-colors"
                    >
                      <Trash size={18} weight="light" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-[#EBEBEB] px-6 py-5 space-y-3 bg-white">
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-[#6B705C]">Subtotal</span>
              <span className="text-sm text-[#4A554E]">{total.toFixed(2)} €</span>
            </div>
            {packOff > 0 && (
              <div
                data-testid="cart-pack-discount"
                className="flex justify-between items-baseline text-[#8A9A6F]"
              >
                <span className="text-xs inline-flex items-center gap-1.5">
                  <Tag size={13} weight="light" />
                  Pack {folderQty} carpetas −{packOff}%
                </span>
                <span className="text-sm">−{packDiscount.toFixed(2)} €</span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-2 border-t border-[#EBEBEB]">
              <span className="text-sm text-[#2D3732]">Total</span>
              <span data-testid="cart-total" className="font-display text-2xl text-[#2D3732]">
                {grandTotal.toFixed(2)} €
              </span>
            </div>
            <p className="text-xs text-[#9CA3AF]">
              {pricing?.shipping
                ? allMagnets
                  ? `+ Envío desde ${pricing.shipping.price_magnet_es.toFixed(2)} € (según destino) en el siguiente paso.`
                  : `+ Envío desde ${pricing.shipping.price_es.toFixed(2)} € (según destino) en el siguiente paso.`
                : "Envío calculado en el siguiente paso."}
              {" Tienes código promocional en el checkout."}
            </p>
            <p className="text-[0.65rem] text-[#9CA3AF] text-center">
              Aceptamos tarjeta · PayPal · Apple Pay · Google Pay · Bizum
            </p>
            <div>
              <input
                data-testid="cart-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Tu email (opcional)"
                className="w-full bg-[#FAF9F6] border border-[#EBEBEB] rounded-full px-5 py-3 text-sm text-[#2D3732] focus:outline-none focus:ring-2 focus:ring-[#A3B18A] placeholder:text-[#9CA3AF]"
              />
              <p className="text-[0.65rem] text-[#9CA3AF] mt-1.5 px-2">
                Déjanos tu email y te guardamos el carrito por si lo dejas a medias.
              </p>
            </div>
            <button
              data-testid="checkout-button"
              onClick={checkout}
              disabled={loading}
              className="w-full bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full py-4 text-sm tracking-wide transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <SpinnerGap size={18} className="animate-spin" /> Redirigiendo…
                </>
              ) : (
                "Finalizar compra"
              )}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
